// Client-side ZIP → US state. Providers are licensed by state, so we derive the state
// from the first 3 ZIP digits (ZCTA prefix ranges) and send BOTH zip and state to the
// API. This is a coarse, offline map — good enough to pick a state for licensure
// filtering; it is not address validation.
type Range = [number, number, string];

// First-3-digit prefix ranges → USPS state code. Ordered, non-overlapping.
const RANGES: Range[] = [
  [0, 0, 'NY'], // 00501/00544 (IRS, NY) — edge cases
  [6, 6, 'PR'],
  [7, 9, 'PR'],
  [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'],
  [50, 54, 'VT'], [55, 55, 'MA'], [56, 59, 'VT'],
  [60, 69, 'CT'], [70, 89, 'NJ'],
  [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'],
  [200, 205, 'DC'], [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'],
  [270, 289, 'NC'], [290, 299, 'SC'], [300, 319, 'GA'], [320, 349, 'FL'],
  [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'],
  [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'],
  [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'], [569, 599, 'MT'],
  [570, 577, 'SD'], [580, 588, 'ND'], [590, 599, 'MT'],
  [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'], [680, 693, 'NE'],
  [700, 714, 'LA'], [716, 729, 'AR'], [730, 749, 'OK'], [750, 799, 'TX'],
  [800, 816, 'CO'], [820, 831, 'WY'], [832, 838, 'ID'], [840, 847, 'UT'],
  [850, 865, 'AZ'], [870, 884, 'NM'], [889, 898, 'NV'],
  [900, 961, 'CA'], [967, 968, 'HI'], [970, 979, 'OR'], [980, 994, 'WA'],
  [995, 999, 'AK'],
];

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', PR: 'Puerto Rico', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

// UK postcode — standard outward+inward pattern (e.g. "SW1A 1AA", "M1 1AE", "B33 8TH").
// Space is optional; case-insensitive.
const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

// US + UK are the two telehealth markets (they decide the LatePoint booking host).
// OTHER = a recognisable postcode from anywhere else: the plan works globally, so we let
// them through, but there's no local PT/booking for their region yet.
export type Country = 'US' | 'GB' | 'OTHER';

// Classify a postal code, or null while it's too short to act on (so we never advance an
// empty/one-char field). We deliberately DON'T hold back short numeric entries as "a US ZIP
// still being typed" — plenty of countries have 3–4 char postcodes (e.g. Australia is 4
// digits), and dead-ending them at this step is worse than the rare case of a US user who
// submits a partial ZIP. US=5 digits and UK are matched exactly; everything else is OTHER.
export function classifyCountry(raw: string): Country | null {
  const s = (raw || '').trim();
  if (s.length < 3) return null;
  if (/^\d{5}(-\d{4})?$/.test(s)) return 'US';
  if (UK_POSTCODE_RE.test(s)) return 'GB';
  return 'OTHER';
}

// Human label for the recognised region: the state for US, the country for UK. UK has no
// state-by-state licensure rule, so we only resolve a state for US codes; OTHER shows none.
export function regionLabel(raw: string): string | null {
  const c = classifyCountry(raw);
  if (c === 'US') return stateName(zipToStateCode(raw));
  if (c === 'GB') return 'United Kingdom';
  return null;
}

export function zipToStateCode(zip: string): string | null {
  const digits = (zip || '').replace(/\D/g, '');
  if (digits.length < 5) return null;
  const prefix = Number(digits.slice(0, 3));
  for (const [lo, hi, code] of RANGES) {
    if (prefix >= lo && prefix <= hi) return code;
  }
  return null;
}

export function stateName(code: string | null): string | null {
  return code ? STATE_NAMES[code] ?? null : null;
}

// All US state/territory names, alphabetical — the provider-apply licensing
// multi-selects use full names (the API stores licensedStates as full names).
export const US_STATE_NAMES: string[] = [...new Set(Object.values(STATE_NAMES))].sort();
